import { useEffect } from 'react';
import { useErrorNotifier, useFirstRender } from '../../../utils/hooks';
import { getSavedGeolocation } from '../../../utils/saved-geolocation';
import Select from '../select';
import {
  geocodingMethod,
  ipLocationMethod,
  LocationInputMethod,
  preciseLocationMethod,
  savedGeolocationMethod
} from './input-methods';
import { useLocationPickerState } from './state';

interface CompactLocationPickerProps {
  onChange: (point: { lat: number; lng: number }) => void;
  startDirty?: boolean;
  alignDropdown?: 'right' | 'left';
  /**
   * If true, the location picker will choose the most accurate user-interaction-free input method and immediately
   * call it to get a geolocation as soon as possible.
   */
  eager?: boolean;
  /**
   * Disables the saved location option. Useful when making an input for the saved location itself.
   */
  disableSavedLocationOption?: boolean;
}

const CompactLocationPicker: React.FC<CompactLocationPickerProps> = ({
  onChange,
  startDirty,
  alignDropdown = 'left',
  eager,
  disableSavedLocationOption
}) => {
  // all location input methods available, organized by most desirable to least
  // server-safe only (otherwise hydration error when it changes on client)
  const locationInputMethods: LocationInputMethod[] = [preciseLocationMethod, geocodingMethod, ipLocationMethod].filter(
    (x: LocationInputMethod | false): x is LocationInputMethod => !!x
  );
  const preferredInputMethod = locationInputMethods[0];
  const preferredEagerInputMethod = locationInputMethods.filter(
    method => 'eager' in method && !method.requiresUserInteraction
  )[0];
  const { method, switchInputMethod, inputValue, setInputValue, dirty, chooseLocation } = useLocationPickerState(
    eager ? preferredEagerInputMethod : preferredInputMethod,
    startDirty ?? false
  );
  const locationErrorHandler = useErrorNotifier();
  useFirstRender(() => {
    if (
      typeof window !== 'undefined' &&
      getSavedGeolocation() != null &&
      !disableSavedLocationOption &&
      savedGeolocationMethod
    ) {
      const clientLocationInputMethods = [
        savedGeolocationMethod,
        preciseLocationMethod,
        geocodingMethod,
        ipLocationMethod
      ].filter((x: LocationInputMethod | false): x is LocationInputMethod => !!x);
      const clientPreferredInputMethod = clientLocationInputMethods[0];
      const clientPreferredEagerInputMethod = locationInputMethods.filter(
        m => 'eager' in m && !m.requiresUserInteraction
      )[0];
      switchInputMethod(eager ? clientPreferredEagerInputMethod : clientPreferredInputMethod);
    }
  });
  useEffect(() => {
    if (eager) {
      chooseLocation().then(onChange).catch(locationErrorHandler);
    }
    // missing chooseLocation, onChange
    // It shouldn't reload the location if either of those change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eager]);
  return (
    <div className='flex flex-wrap bg-gray-700 rounded input-background'>
      <Select
        option={method.label}
        options={locationInputMethods.map(inputMethod => inputMethod.label)}
        labelText='Choose a location input method'
        setOption={option =>
          switchInputMethod(locationInputMethods.find(m => m.label === option)!).catch(locationErrorHandler)
        }
        alignDropdown={alignDropdown}
        className='flex-1'
      />
      <div className='flex flex-1 justify-between'>
        {'input' in method && (
          <input
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder={method.input.placeholder}
            className='flex-1 w-full'
          />
        )}
        {dirty && (
          <button
            type='button'
            className='px-4 font-medium text-sm self-stretch hover:bg-gray-600 touched:bg-gray-600 rounded'
            onClick={() => chooseLocation().then(onChange).catch(locationErrorHandler)}
          >
            OK
          </button>
        )}
      </div>
      <style jsx>{`
        .flex-grow-lots {
          flex: 9999 1 0%;
        }
      `}</style>
    </div>
  );
};

export default CompactLocationPicker;
